import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GlossaryLayout from "@/components/GlossaryLayout";
import useSEO from "@/hooks/useSEO";

const ARTIFACT_TYPES = [
  {
    name: "Escalation rules",
    description:
      "Structured conditions that determine when and how a case is routed to a different team or tier. Example: \"IF payment fails after 3DS authentication AND failure code is acquirer_decline, THEN escalate to Tier 2 Payments within 4 hours.\"",
  },
  {
    name: "Decision trees",
    description:
      "Branching logic that guides an agent or AI system through a resolution path. Decision trees are high-risk artifacts because small ambiguities compound across thousands of interactions.",
  },
  {
    name: "Macros and response templates",
    description:
      "Pre-written responses or action sequences that agents apply to specific ticket types. When used to train or prompt AI, these carry the voice and reasoning of their original author — creating named-person phrasing risks.",
  },
  {
    name: "Knowledge base articles",
    description:
      "Explanatory content that describes how a product, process, or policy works. KB articles fed to retrieval-augmented AI must be classified by contributor class before use.",
  },
  {
    name: "Policy documents",
    description:
      "Formal statements of company rules, procedures, or standards. These are typically Class 1 artifacts — clear ownership, lowest review burden — but require version control when used as AI context.",
  },
  {
    name: "Extracted operational patterns",
    description:
      "Recurring behaviours or resolutions derived from support tickets, call recordings, or CRM data. These are Class 2 artifacts: the underlying work is company-owned, but the pattern was inferred, not explicitly authored.",
  },
];

const FAQS = [
  {
    q: "Is a knowledge artifact the same as a training document?",
    a: "Not exactly. A training document is a broad term for any content used in model training. A knowledge artifact is more specific: it is a discrete, structured unit of operational knowledge that shapes AI behaviour — whether as a training signal, a retrieval source, a system prompt, or a decision rule. The distinction matters because governance operates at the artifact level, not the document level.",
  },
  {
    q: "What makes an artifact 'candidate' vs 'published'?",
    a: "A candidate artifact is an extracted pattern that has been identified for review but not yet approved for use in an AI system. A published artifact has passed the review workflow and is live. Governance tracks both states — and the transition between them — in the evidence record.",
  },
  {
    q: "Can a knowledge artifact be revoked after publication?",
    a: "Yes — and revoking it is one of the most important governance operations. When an artifact is revoked, it is frozen and removed from active AI context. Downstream impact is analysed: which AI behaviours depended on it, which cases were affected, and whether any model retraining or prompt updates are required.",
  },
  {
    q: "Who owns a knowledge artifact?",
    a: "Contributor class determines this. A Class 1 artifact has clear, explicit ownership — typically a team or a role rather than an individual. A Class 2 artifact is derived from operational data, and ownership is attributed to the organisation rather than any individual agent. Class 3 artifacts — inferred from individual behaviour — are out of scope for most AI governance frameworks.",
  },
];

export default function AiKnowledgeArtifact() {
  useSEO({
    title: "What is an AI Knowledge Artifact? — ProofMark Glossary",
    description:
      "An AI knowledge artifact is a discrete unit of operational knowledge — an escalation rule, decision tree, macro, or KB article — that shapes how an AI system behaves. Learn what qualifies, why governance operates at this level, and how artifacts are classified.",
    path: "/glossary/ai-knowledge-artifact",
  });

  useEffect(() => {
    const scriptId = "proofmark-jsonld-defined-term";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: "AI Knowledge Artifact",
      description:
        "A discrete, reviewable unit of operational knowledge — such as an escalation rule, decision tree, macro, or knowledge base article — that is extracted for use in an AI system and subject to governance review before deployment.",
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "ProofMark AI Governance Glossary",
        url: "https://proofmark-frontend-production.up.railway.app/glossary/ai-knowledge-artifact",
      },
    });
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <GlossaryLayout>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">ProofMark</Link>
        <span>/</span>
        <span>Glossary</span>
        <span>/</span>
        <span className="text-foreground">AI Knowledge Artifact</span>
      </nav>

      {/* Hero */}
      <div className="mb-10">
        <span className="tracked-caps">AI Governance Glossary</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-4">AI Knowledge Artifact</h1>
        <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
          A discrete, reviewable unit of operational knowledge that shapes how an AI system behaves
          — and the fundamental unit at which AI knowledge governance operates.
        </p>
      </div>

      {/* Definition box */}
      <div className="border-l-4 border-brand pl-6 py-4 bg-brand/5 rounded-r-lg mb-12">
        <p className="text-xs tracked-caps text-muted-foreground mb-2">Definition</p>
        <p className="text-base leading-relaxed">
          An <strong>AI knowledge artifact</strong> is a structured, identifiable piece of operational knowledge —
          an escalation rule, decision tree, response macro, knowledge base article, or extracted
          operational pattern — that has been or may be used as a training signal, retrieval source,
          system prompt, or decision rule in an AI system. Governance operates at this level because
          artifacts carry authorship, policy implications, and downstream AI behaviour that raw data
          does not.
        </p>
      </div>

      {/* Why artifact-level governance */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">
          Why governance operates at the artifact level
        </h2>
        <div className="prose-like space-y-4 max-w-3xl text-muted-foreground leading-relaxed">
          <p>
            Most AI governance frameworks focus on models or data pipelines. Artifact-level
            governance is different: it treats each discrete unit of operational knowledge as a
            governed object with its own ownership, review history, and revocation rights.
          </p>
          <p>
            The reason this granularity matters is that AI behaviour is not shaped by data in bulk —
            it is shaped by specific patterns, rules, and examples that recur and generalise. An
            escalation rule that routes payment failures incorrectly does not affect one ticket; it
            shapes every payment failure the AI system encounters. Governing the artifact before it
            is deployed prevents that pattern from propagating at scale.
          </p>
          <p>
            Artifact-level governance also enables precise revocation. When a rule is found to be
            wrong, discriminatory, or outdated, the organisation can revoke that specific artifact
            and analyse exactly which AI behaviours depended on it — without having to retrain or
            re-audit an entire model.
          </p>
        </div>
      </section>

      {/* Types of knowledge artifacts */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">Types of knowledge artifacts</h2>
        <div className="space-y-4 max-w-3xl">
          {ARTIFACT_TYPES.map((t) => (
            <div key={t.name} className="border border-border rounded-lg p-5 bg-card">
              <p className="font-medium text-sm mb-1.5">{t.name}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Knowledge artifacts vs raw data */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">
          Knowledge artifacts vs raw data
        </h2>
        <div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed">
          <p>
            Raw data — support tickets, call transcripts, CRM records — is the source material from
            which knowledge artifacts are extracted. The extraction step is significant: it is where
            operational patterns are identified, structured, and transformed into something an AI
            system can act on.
          </p>
          <p>
            Raw data governance addresses storage, access, and retention. Artifact governance
            addresses something different: the epistemological question of whether a given pattern
            is correct, attributable, compliant, and safe to encode into AI behaviour. These are
            not the same problem.
          </p>
          <p>
            A support ticket is governed by GDPR. An escalation rule derived from that ticket is
            governed by your AI knowledge policy. Both matter — but confusing them leads to
            governance gaps where the derived rule is never reviewed.
          </p>
        </div>
      </section>

      {/* Lifecycle */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">The artifact lifecycle</h2>
        <div className="max-w-3xl">
          <div className="relative">
            {[
              { stage: "Extraction", desc: "A recurring pattern is identified in operational data and surfaced as a candidate artifact." },
              { stage: "Classification", desc: "The artifact is assigned a contributor class (Class 1, 2, or 3) based on its source and authorship characteristics." },
              { stage: "Review", desc: "Designated reviewers examine the artifact for policy violations, accuracy, and AI-readiness. Evidence is recorded." },
              { stage: "Approval or rejection", desc: "The artifact is approved for publication or rejected with documented reasoning." },
              { stage: "Publication", desc: "The artifact becomes live — available as a training signal, retrieval document, or AI decision rule." },
              { stage: "Revocation", desc: "If the artifact is found to be incorrect, outdated, or non-compliant, it is revoked and downstream AI behaviours are audited." },
            ].map((s, i) => (
              <div key={s.stage} className="flex gap-4 mb-5">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-7 h-7 rounded-full border-2 border-brand bg-brand/10 flex items-center justify-center text-xs font-medium text-brand">
                    {i + 1}
                  </div>
                  {i < 5 && <div className="w-px flex-1 bg-border mt-1" />}
                </div>
                <div className="pb-5">
                  <p className="font-medium text-sm mb-1">{s.stage}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related terms */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">Related terms</h2>
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          <Link
            to="/glossary/contributor-class"
            className="border border-border rounded-lg p-4 bg-card hover:border-brand/40 transition-colors group"
          >
            <p className="font-medium text-sm group-hover:text-brand transition-colors">Contributor Class →</p>
            <p className="text-xs text-muted-foreground mt-1">
              How artifacts are classified by source ownership and authorship type.
            </p>
          </Link>
          <Link
            to="/glossary/ai-knowledge-governance"
            className="border border-border rounded-lg p-4 bg-card hover:border-brand/40 transition-colors group"
          >
            <p className="font-medium text-sm group-hover:text-brand transition-colors">AI Knowledge Governance →</p>
            <p className="text-xs text-muted-foreground mt-1">
              The practice of governing what knowledge enters AI systems and on what basis.
            </p>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">Frequently asked questions</h2>
        <div className="space-y-5 max-w-3xl">
          {FAQS.map((faq) => (
            <div key={faq.q} className="border border-border rounded-lg p-5 bg-card">
              <p className="font-medium text-sm mb-2">{faq.q}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="border border-brand/30 rounded-lg p-6 bg-brand/5 max-w-2xl">
        <p className="font-medium mb-2">Govern knowledge artifacts in your AI systems</p>
        <p className="text-sm text-muted-foreground mb-4">
          ProofMark extracts candidate artifacts from your operational data, routes them through a
          structured review workflow, and maintains an immutable evidence record for every
          approval, rejection, and revocation.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Request early access
          </Link>
          <Link
            to="/tools/policy-checker"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand/40 transition-colors"
          >
            Try the free Policy Checker →
          </Link>
        </div>
      </div>
    </GlossaryLayout>
  );
}
